import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Radio, RadioGroup, Stack } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { formatToPhone } from 'brazilian-values';
import { useIntegration } from '../../../../hooks/Integration';
import { useManager } from '../../../../hooks/Manager';
import { useModal } from '../../../../hooks/Modal';
import { usePickup } from '../../../../hooks/Pickup';
import { parseCpfCnpj } from '../../../../helpers/parseValues';
import { ReverseCollectContext } from '../..';
import { ModalTitle } from './styled';

export const ModalChangeRequesterData = () => {
  const [radio, setRadio] = useState('');
  const [searchingForRestriction, setSearchingForRestriction] = useState(false);

  const { verifyUserRestriction } = useIntegration();
  const {
    getPickupManagerParameterization,
    getPickupManagerBranchValidations
  } = useManager();
  const { closeModal, openModal, Modal } = useModal();
  const { cancelReversePickup } = usePickup();

  const {
    addressList,
    selectedAddressIndex,
    setSelectedAddressIndex,
    setProtocolCollectCanceled
  } = useContext(ReverseCollectContext);

  const handleChangeRadio = (event) => {
    setRadio(event.target.value);
  };

  const handleSetAddress = async () => {
    const index = parseInt(radio.replace('address', ''), 10);

    try {
      setSearchingForRestriction(true);

      const pickupManagerResponse = await getPickupManagerParameterization({
        branchId: 1,
        transportTypeId: 9,
        pickupTypeId: 1,
        inputTypeId: 5
      });

      if (pickupManagerResponse?.length > 0) {
        const validationResponse = await getPickupManagerBranchValidations({
          branchParameterizationId:
            pickupManagerResponse[0]?.branchParameterizationId
        });

        if (validationResponse?.length > 0) {
          if (
            validationResponse[0]?.validatePendingCharge ||
            validationResponse[0]?.validateLegalPending
          ) {
            const userHasRestriction = await verifyUserRestriction({
              document: parseCpfCnpj(addressList[index].document)
            });

            if (userHasRestriction?.isBlocked) {
              const address = {
                zipPostalCode: addressList[index].zipPostalCode,
                streetAddress: addressList[index].streetAddress,
                streetAddressNumber: addressList[index].streetAddressNumber,
                additionalInformation: '',
                suburb: addressList[index].suburb,
                city: addressList[index].city,
                state: addressList[index].state,
                country: ''
              };

              const cancelResponse = await cancelReversePickup({
                data: {
                  pickup: {
                    requesterCorporation: {
                      requesterType: 'sender',
                      document: parseCpfCnpj(addressList[index].document),
                      name: addressList[index].name,
                      address,
                      contactList: [
                        {
                          name: addressList[index].contactName,
                          phoneNumber: addressList[index].contactNumber,
                          extension: addressList[index].contactExtension || '',
                          email: addressList[index].contactEmail
                        }
                      ]
                    },
                    shipmentList: [
                      {
                        transportTypeId: null,
                        freightTypeId: null,
                        senderCorporation: null,
                        receiverCorporation: {
                          document: parseCpfCnpj(addressList[index].document),
                          name: addressList[index].name,
                          address,
                          contactList: [
                            {
                              name: addressList[index].contactName,
                              phoneNumber: addressList[index].contactNumber,
                              extension:
                                addressList[index].contactExtension || '',
                              email: addressList[index].contactEmail
                            }
                          ]
                        },
                        shipmentAvailability: null,
                        shipmentPackageList: null,
                        taxInformation: null
                      }
                    ],
                    pickupCancelType: 'Restrição Financeira ou Jurídica'
                  }
                }
              });

              if (cancelResponse.status === 200) {
                setProtocolCollectCanceled(cancelResponse.data.data.protocol);
              }

              openModal('modalFinancialRestriction');

              return;
            }
          }
        }
      }

      setSelectedAddressIndex(index);
      closeModal();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);

      setSearchingForRestriction(false);
    }
  };

  const handleCloseModal = () => {
    closeModal();
  };

  useEffect(() => {
    setRadio(`address${selectedAddressIndex}`);
  }, []);

  return (
    <Modal preventClose>
      <Box
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          maxHeight: '86vh',
          overflow: 'auto'
        }}
      >
        <ModalTitle>Pesquisar - Dados do Solicitante</ModalTitle>

        <RadioGroup name="collectAddress">
          <Stack spacing={3}>
            {addressList.map(
              (
                {
                  name,
                  document,
                  city,
                  state,
                  streetAddress,
                  streetAddressNumber,
                  additionalInformation,
                  suburb,
                  zipPostalCode,
                  contactName,
                  contactNumber,
                  contactExtension,
                  contactEmail
                },
                index
              ) => (
                <Stack
                  // eslint-disable-next-line react/no-array-index-key
                  key={`address${index}`}
                  direction="row"
                  spacing={2}
                  alignItems="center"
                >
                  <Radio
                    name="collectAddress"
                    id={`address${index}`}
                    value={`address${index}`}
                    checked={radio === `address${index}`}
                    disabled={
                      !city ||
                      !state ||
                      !streetAddress ||
                      !streetAddressNumber ||
                      !suburb ||
                      !zipPostalCode ||
                      !contactName ||
                      !contactNumber ||
                      !contactEmail
                    }
                    onChange={handleChangeRadio}
                  />
                  <label
                    htmlFor={`address${index}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {name} - {document}
                    <br />
                    CEP: {zipPostalCode}
                    {streetAddress && <>, {streetAddress}</>}
                    {streetAddressNumber && <>, {streetAddressNumber}</>}
                    {additionalInformation && <>, {additionalInformation}</>}
                    {suburb && <>, {suburb}</>}
                    {city && <>, {city}</>}
                    {state && <>-{state}</>}
                    <br />
                    {(contactName ||
                      contactNumber ||
                      contactExtension ||
                      contactEmail) && (
                      <p>
                        CONTATO: {contactName}
                        {contactNumber && (
                          <>
                            {contactName && <>, </>}
                            {formatToPhone(
                              String(contactNumber).padStart(10, '0')
                            )}
                          </>
                        )}
                        {contactExtension && (
                          <>
                            {(contactName || contactNumber) && <>, </>}
                            RAMAL {contactExtension}
                          </>
                        )}
                        {contactEmail && (
                          <>
                            {(contactName ||
                              contactNumber ||
                              contactExtension) && <>, </>}
                            {contactEmail}
                          </>
                        )}
                      </p>
                    )}
                  </label>
                </Stack>
              )
            )}
          </Stack>
        </RadioGroup>

        <Stack direction="row" spacing={2} marginTop={1}>
          <Button
            type="button"
            variant="contained"
            color="secondary"
            onClick={handleCloseModal}
            disabled={!!searchingForRestriction}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="button"
            variant="contained"
            onClick={handleSetAddress}
            loading={!!searchingForRestriction}
            disabled={!!searchingForRestriction}
          >
            Confirmar
          </LoadingButton>
        </Stack>
      </Box>
    </Modal>
  );
};
